import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { settingsService } from '../../services/dataService';
import styles from './SettingsModalitiesPage.module.css';

const initialForm = {
  vendor_percentage: '',
  facility_percentage: '',
  start_date: '',
  end_date: '',
};

const normalizeApiError = (error, fallback) => (
  error?.response?.data?.detail || error?.detail || fallback
);

const formatDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString() : '-');

const rangesOverlap = (leftStart, leftEnd, rightStart, rightEnd) => (
  leftStart <= rightEnd && leftEnd >= rightStart
);

const SettingsRevenueDistributionPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState('');
  const [form, setForm] = useState(initialForm);

  const sortedItems = useMemo(() => (
    [...items].sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)))
  ), [items]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await settingsService.getRevenueDistributions();
      setItems(Array.isArray(response) ? response : []);
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to load revenue distributions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setForm(initialForm);
    setEditId('');
    setShowForm(false);
  };

  const startCreate = () => {
    setError('');
    setEditId('');
    setForm(initialForm);
    setShowForm(true);
  };

  const startEdit = (item) => {
    setError('');
    setEditId(item.id);
    setForm({
      vendor_percentage: String(item.vendor_percentage ?? ''),
      facility_percentage: String(item.facility_percentage ?? ''),
      start_date: item.start_date || '',
      end_date: item.end_date || '',
    });
    setShowForm(true);
  };

  const validateForm = () => {
    const vendor = Number(form.vendor_percentage);
    const facility = Number(form.facility_percentage);

    if (!Number.isFinite(vendor) || vendor < 0 || vendor > 100) {
      return 'Vendor percentage must be between 0 and 100';
    }
    if (!Number.isFinite(facility) || facility < 0 || facility > 100) {
      return 'Facility percentage must be between 0 and 100';
    }
    if (!form.start_date || !form.end_date) {
      return 'Start date and end date are required';
    }
    if (form.end_date < form.start_date) {
      return 'End date must be on or after start date';
    }

    const overlapping = items.find((item) => (
      item.id !== editId
      && item.active
      && rangesOverlap(form.start_date, form.end_date, item.start_date, item.end_date)
    ));
    if (overlapping) {
      return `Date range overlaps ${formatDate(overlapping.start_date)} - ${formatDate(overlapping.end_date)}`;
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      vendor_percentage: Number(form.vendor_percentage),
      facility_percentage: Number(form.facility_percentage),
      start_date: form.start_date,
      end_date: form.end_date,
      active: true,
    };

    setSaving(true);
    setError('');
    try {
      if (editId) {
        await settingsService.updateRevenueDistribution(editId, payload);
      } else {
        await settingsService.createRevenueDistribution(payload);
      }
      resetForm();
      await loadItems();
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to save revenue distribution'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Revenue Distribution</h1>
        <div className={styles.headerActions}>
          <Link className={styles.backLink} to="/settings">Back to Settings</Link>
          <button type="button" className={styles.addButton} onClick={startCreate}>
            Add Distribution
          </button>
        </div>
      </header>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <h2>Active Distribution Periods</h2>
        {loading ? <p>Loading revenue distributions...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Facility</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => (
                <tr key={item.id}>
                  <td>{Number(item.vendor_percentage).toFixed(2)}%</td>
                  <td>{Number(item.facility_percentage).toFixed(2)}%</td>
                  <td>{formatDate(item.start_date)}</td>
                  <td>{formatDate(item.end_date)}</td>
                  <td>{item.active ? 'active' : 'inactive'}</td>
                  <td className={styles.actions}>
                    <button type="button" onClick={() => startEdit(item)}>Edit</button>
                  </td>
                </tr>
              ))}
              {!loading && sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={6}>No active revenue distributions found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {showForm ? (
        <div className={styles.modalOverlay}>
          <form className={styles.modal} onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <h2>{editId ? 'Edit Distribution' : 'Add Distribution'}</h2>
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={resetForm} disabled={saving}>Cancel</button>
              </div>
            </div>
            <div className={styles.grid3}>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="Vendor %"
                value={form.vendor_percentage}
                onChange={(event) => setForm((value) => ({ ...value, vendor_percentage: event.target.value }))}
              />
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="Facility %"
                value={form.facility_percentage}
                onChange={(event) => setForm((value) => ({ ...value, facility_percentage: event.target.value }))}
              />
              <input
                type="date"
                value={form.start_date}
                onChange={(event) => setForm((value) => ({ ...value, start_date: event.target.value }))}
              />
              <input
                type="date"
                value={form.end_date}
                onChange={(event) => setForm((value) => ({ ...value, end_date: event.target.value }))}
              />
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default SettingsRevenueDistributionPage;
